import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useUserPortfolio } from '../../hooks/contract/useUserPortfolio';
import { useAllPlans, FALLBACK_PLANS } from '../../hooks/contract/useAllPlans';
import { useLivePending } from '../../hooks/contract/useLivePending';
import { useClaim, useClaimReferral } from '../../hooks/contract/useWriteActions';
import { useUserEventLogs } from '../../hooks/indexer/useUserEventLogs';
import KpiCard from '../../components/dashboard/KpiCard';
import { formatUsdt, formatDuration, matureAt, planDurationSec, formatPlanLabel } from '../../lib/format';

const Overview = () => {
  const { t } = useTranslation();
  const { deposits, userInfo, pendingAll, isReady } = useUserPortfolio();
  const { data: onChainPlans } = useAllPlans();
  const plans = onChainPlans ?? FALLBACK_PLANS;
  const livePending = useLivePending(deposits, plans, pendingAll);
  const { data: events = [] } = useUserEventLogs();

  // Суммируем весь забранный доход (Claimed.netToUser) + все забранные награды (ReferralClaimed.amount).
  // Withdrawn.auto-claim в моке записывается отдельным Claimed-событием, поэтому считаем только Claimed.
  const lifetimeEarned = useMemo(() => {
    let total = 0n;
    for (const ev of events) {
      if (ev?.type === 'Claimed' && ev.args?.netToUser !== undefined) {
        total += BigInt(ev.args.netToUser);
      } else if (ev?.type === 'ReferralClaimed' && ev.args?.amount !== undefined) {
        total += BigInt(ev.args.amount);
      }
    }
    return total;
  }, [events]);

  const claimAction = useClaim();
  const refClaimAction = useClaimReferral();
  const [batchState, setBatchState] = useState({ running: false, done: 0, total: 0 });

  const activeDeposits = useMemo(
    () => deposits?.filter((d) => d.active) ?? [],
    [deposits],
  );

  const totalPrincipal = useMemo(
    () => activeDeposits.reduce((s, d) => s + BigInt(d.amount), 0n),
    [activeDeposits],
  );

  const pendingReferral = userInfo?.pendingReferral ?? 0n;

  const nextMaturity = useMemo(() => {
    if (!activeDeposits.length || !plans.length) return null;
    const now = Math.floor(Date.now() / 1000);
    const upcoming = activeDeposits
      .map((d) => {
        const p = plans[Number(d.planId)];
        if (!p) return null;
        return matureAt({ startedAt: d.startedAt, durationSec: planDurationSec(p) });
      })
      .filter((ts) => ts !== null && ts > now)
      .sort((a, b) => a - b);
    if (!upcoming.length) return null;
    return upcoming[0] - now;
  }, [activeDeposits, plans]);

  const handleClaimAll = async () => {
    const claimable = activeDeposits
      .map((d, originalIdx) => ({ d, originalIdx }))
      .filter(({ d }) => {
        const p = plans[Number(d.planId)];
        if (!p) return false;
        const elapsed = Math.floor(Date.now() / 1000) - Number(d.lastClaimAt);
        return elapsed > 0 && BigInt(d.amount) > 0n;
      });

    if (!claimable.length) return;
    // Мапим активные депозиты обратно на исходные индексы в массиве
    const originalIndices = [];
    deposits.forEach((d, i) => {
      if (d.active) originalIndices.push(i);
    });

    setBatchState({ running: true, done: 0, total: originalIndices.length });
    for (let i = 0; i < originalIndices.length; i++) {
      // eslint-disable-next-line no-await-in-loop
      const res = await claimAction.claim(originalIndices[i]);
      if (!res.success) break;
      setBatchState((s) => ({ ...s, done: s.done + 1 }));
    }
    setBatchState({ running: false, done: 0, total: 0 });
  };

  const canClaimAny = livePending > 0n;
  const canClaimReferral = pendingReferral > 0n;

  return (
    <div className="db-page db-page--overview">
      <div className="db-page__head">
        <h1 className="db-page__title">{t('dashboard.overview.title')}</h1>
        <p className="db-page__subtitle">{t('dashboard.overview.subtitle')}</p>
      </div>

      <div className="kpi-grid">
        <KpiCard
          icon="fa-vault"
          accent="default"
          label={t('dashboard.overview.kpi.principal')}
          value={`${formatUsdt(totalPrincipal)} USDT`}
          hint={
            isReady
              ? t('dashboard.overview.kpi.principalHint', {
                  count: activeDeposits.length,
                })
              : '—'
          }
        />
        <KpiCard
          icon="fa-chart-line"
          accent="gold"
          label={t('dashboard.overview.kpi.pending')}
          value={`${formatUsdt(livePending)} USDT`}
          hint={
            nextMaturity !== null
              ? t('dashboard.overview.kpi.nextMature', {
                  time: formatDuration(nextMaturity),
                })
              : t('dashboard.overview.kpi.noActive')
          }
        />
        <KpiCard
          icon="fa-sack-dollar"
          accent="emerald"
          label={t('dashboard.overview.kpi.refClaimable')}
          value={`${formatUsdt(pendingReferral)} USDT`}
          hint={t('dashboard.overview.kpi.refHint')}
        />
        <KpiCard
          icon="fa-trophy"
          accent="default"
          label={t('dashboard.overview.kpi.lifetime')}
          value={`${formatUsdt(lifetimeEarned)} USDT`}
          hint={t('dashboard.overview.kpi.lifetimeHint', {
            count: activeDeposits.length,
          })}
        />
      </div>

      <div className="db-actions">
        <Link to="/dashboard/new" className="sf-btn sf-btn--gold sf-btn--lg">
          <i className="fas fa-plus" />
          {t('dashboard.overview.actions.newDeposit')}
        </Link>
        <button
          type="button"
          className="sf-btn sf-btn--outline sf-btn--lg"
          onClick={handleClaimAll}
          disabled={!canClaimAny || batchState.running}
        >
          {batchState.running
            ? t('dashboard.overview.actions.claimProgress', {
                done: batchState.done,
                total: batchState.total,
              })
            : t('dashboard.overview.actions.claimAll')}
        </button>
        <button
          type="button"
          className="sf-btn sf-btn--outline sf-btn--lg"
          onClick={() => refClaimAction.claim()}
          disabled={!canClaimReferral || refClaimAction.status === 'signing' || refClaimAction.status === 'confirming'}
        >
          {t('dashboard.overview.actions.claimRef')}
        </button>
      </div>

      {!activeDeposits.length && (
        <div className="db-empty">
          <div className="db-empty__icon" aria-hidden="true">
            <i className="fas fa-seedling" />
          </div>
          <h3>{t('dashboard.overview.empty.title')}</h3>
          <p>{t('dashboard.overview.empty.desc')}</p>
          <Link to="/dashboard/new" className="sf-btn sf-btn--gold">
            {t('dashboard.overview.empty.cta')}
          </Link>
        </div>
      )}

      {activeDeposits.length > 0 && (
        <div className="db-block">
          <div className="db-block__head">
            <h3>{t('dashboard.overview.recent.title')}</h3>
            <Link to="/dashboard/deposits" className="db-block__link">
              {t('dashboard.overview.recent.viewAll')}
              <i className="fas fa-arrow-right" />
            </Link>
          </div>
          <div className="db-recent">
            {activeDeposits.slice(0, 3).map((d, idx) => {
              const plan = plans[Number(d.planId)];
              const endsAt = plan
                ? matureAt({ startedAt: d.startedAt, durationSec: planDurationSec(plan) })
                : 0;
              const remaining = Math.max(0, endsAt - Math.floor(Date.now() / 1000));
              return (
                <div key={idx} className="db-recent__row">
                  <div className="db-recent__info">
                    <span className="db-recent__amt mono">{formatUsdt(d.amount)} USDT</span>
                    <span className="db-recent__plan">
                      {plan ? formatPlanLabel(plan, t) : '—'}
                    </span>
                  </div>
                  <div className="db-recent__meta">
                    {remaining > 0
                      ? t('dashboard.overview.recent.matures', { time: formatDuration(remaining) })
                      : t('dashboard.overview.recent.matured')}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Overview;
